import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import { formatGel } from '@/pages/accounting/books/chartOfAccounts/utils';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    disagreementTetri,
    sumSide,
    type RegisterTieOut,
    type TieOutSide,
} from '../../fixedAssetsMeta';

type TieOutBarProps = {
    /** Null while the reconciliation is loading **or** has failed. */
    tieOut: RegisterTieOut | null;
    /** Non-null when the tie-out could not be read at all. */
    error?: string | null;
    onRetry?: () => void;
};

const Side: React.FC<{
    label: string;
    accountsLabel: string;
    sides: TieOutSide[];
    slot: string;
    differenceLabel: string;
}> = ({ label, accountsLabel, sides, slot, differenceLabel }) => {
    const registerTetri = sumSide(sides, 'registerTetri');
    const ledgerTetri = sumSide(sides, 'ledgerTetri');
    const agrees = sides.every((side) => side.agrees);
    const figure = { registerTetri, ledgerTetri, agrees };
    return (
        <div className="flex flex-col gap-1">
            <span className="text-xs font-medium uppercase tracking-wide text-muted">
                {label}
            </span>
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span
                    className="text-base font-semibold tabular-nums text-foreground"
                    {...getNodeTestIdProp(
                        `fixed-assets-tie-out-register-${slot}`,
                    )}
                >
                    {formatGel(figure.registerTetri)}
                </span>
                <Icon
                    aria-hidden
                    className="size-3.5 shrink-0 text-muted"
                    icon={
                        figure.agrees
                            ? 'gravity-ui:equal'
                            : 'gravity-ui:arrow-right-arrow-left'
                    }
                />
                <span
                    className="text-base font-semibold tabular-nums text-foreground"
                    {...getNodeTestIdProp(
                        `fixed-assets-tie-out-ledger-${slot}`,
                    )}
                >
                    {formatGel(figure.ledgerTetri)}
                </span>
            </div>
            <span className="text-xs text-muted">{accountsLabel}</span>
            {figure.agrees ? null : (
                <span
                    className="text-xs font-medium text-danger"
                    {...getNodeTestIdProp(
                        `fixed-assets-tie-out-difference-${slot}`,
                    )}
                >
                    {differenceLabel}
                </span>
            )}
        </div>
    );
};

/**
 * The register-to-ledger tie-out — the first-class element, not a debug aid.
 *
 * The register is a subsidiary ledger: its total cost must equal the balance of
 * the cost accounts and its total accumulated depreciation must equal the balance
 * of the contra accounts. This states both comparisons plainly and, when they
 * disagree, shows the exact difference in the danger colour. The state is carried
 * by text and icon as well as colour, like the Balance Sheet's "Balanced ✓".
 *
 * **Both sides are the server's.** `GET /reconciliation` holds the register and
 * the ledger and reports its own `tiesOut` verdict and difference figures; this
 * renders them. The *pending integration* state is gone with the mock register —
 * there is a real ledger to reconcile against now, so the bar either reports
 * agreement or reports a difference that is genuinely there.
 *
 * The standing rule it serves: **no tie-out may report a difference that isn't
 * real, and none may imply agreement it can't verify.** While the reconciliation
 * is loading it says so rather than drawing either — an unknown ledger is not a
 * disagreeing one.
 */
export const TieOutBar: React.FC<TieOutBarProps> = ({
    tieOut,
    error,
    onRetry,
}) => {
    const { t } = useTranslation('pages');

    // A tie-out that could not be read is not a pending one. Saying "Checking…"
    // forever would imply the check is still coming; saying nothing would let a
    // screen whose whole job is reconciliation quietly stop reconciling.
    if (error) {
        return (
            <div
                className="flex flex-wrap items-center gap-3 rounded-xl bg-danger-soft px-4 py-3 text-sm text-danger-soft-foreground"
                {...getNodeTestIdProp('fixed-assets-tie-out-error')}
            >
                <Icon aria-hidden icon="gravity-ui:triangle-exclamation" />
                <span>{t('fixedAssets.tieOut.failed')}</span>
                {onRetry ? (
                    <Button
                        onPress={onRetry}
                        size="sm"
                        variant="outline"
                        {...getNodeTestIdProp('fixed-assets-tie-out-retry')}
                    >
                        {t('fixedAssets.tieOut.retry')}
                    </Button>
                ) : null}
            </div>
        );
    }

    if (tieOut === null) {
        return (
            <div
                className="flex items-center gap-2 rounded-xl bg-surface-secondary px-4 py-3 text-sm text-muted"
                {...getNodeTestIdProp('fixed-assets-tie-out-loading')}
            >
                <Icon aria-hidden icon="gravity-ui:circle-info" />
                {t('fixedAssets.tieOut.loading')}
            </div>
        );
    }

    const listCodes = (codes: string[]): string =>
        codes.length > 0
            ? codes.join(', ')
            : t('fixedAssets.tieOut.noAccounts');

    return (
        <section
            aria-label={t('fixedAssets.tieOut.title')}
            className="flex flex-col gap-4 rounded-xl bg-surface-secondary px-4 py-3 lg:flex-row lg:items-center lg:justify-between"
            {...getNodeTestIdProp('fixed-assets-tie-out')}
        >
            <div className="flex flex-wrap items-start gap-x-10 gap-y-4">
                <Side
                    accountsLabel={t('fixedAssets.tieOut.against', {
                        accounts: listCodes(tieOut.costAccountCodes),
                    })}
                    differenceLabel={t('fixedAssets.tieOut.offBy', {
                        amount: formatGel(disagreementTetri(tieOut.cost)),
                    })}
                    sides={tieOut.cost}
                    label={t('fixedAssets.tieOut.cost')}
                    slot="cost"
                />
                <Side
                    accountsLabel={t('fixedAssets.tieOut.against', {
                        accounts: listCodes(
                            tieOut.accumulatedDepreciationAccountCodes,
                        ),
                    })}
                    differenceLabel={t('fixedAssets.tieOut.offBy', {
                        amount: formatGel(
                            disagreementTetri(tieOut.accumulatedDepreciation),
                        ),
                    })}
                    sides={tieOut.accumulatedDepreciation}
                    label={t('fixedAssets.tieOut.accumulated')}
                    slot="accumulated"
                />
            </div>

            <div
                className={cn(
                    'flex w-fit items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-semibold',
                    tieOut.inAgreement
                        ? 'bg-success-soft text-success-soft-foreground'
                        : 'bg-danger-soft text-danger-soft-foreground',
                )}
                {...getNodeTestIdProp('fixed-assets-tie-out-indicator')}
            >
                <Icon
                    aria-hidden
                    icon={
                        tieOut.inAgreement
                            ? 'gravity-ui:circle-check'
                            : 'gravity-ui:scales'
                    }
                />
                {tieOut.inAgreement
                    ? t('fixedAssets.tieOut.inAgreement')
                    : t('fixedAssets.tieOut.outOfAgreement')}
            </div>
        </section>
    );
};
